import React, { useEffect, useState, useRef, useCallback } from "react"
import MetaTags from "react-meta-tags"
import { Col, Container, Row, Badge, Card, CardBody } from "reactstrap"
//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"
import Datatable from "components/Datatables/datatable"
import { post } from "helpers/api_helper"
import "react-datepicker/dist/react-datepicker.css"
import { Filters } from "../filters"
import moment from "moment"
import { runBatchLoader } from "utils/batchLoader"

const isType = "WhatsApp Notifications"

const WNotificationReport = () => {
  const [rowData, setRowData] = useState([])
  const [filterData, setFilterData] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [whatsappStatus, setWhatsappStatus] = useState("all")
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    search: "",
  })
  const [batchProgress, setBatchProgress] = useState(null)
  const batchCancelRef = useRef(false)
  const isBatchRunningRef = useRef(false)
  const totalRowsRef = useRef(0)
  const lastQueryRef = useRef('')

  useEffect(() => {
    if (rowData?.total_rows) {
      totalRowsRef.current = rowData.total_rows
    }
  }, [rowData])

  const handleCancelBatch = () => {
    batchCancelRef.current = true
    isBatchRunningRef.current = false
    setBatchProgress(prev => prev ? ({ ...prev, active: false, stopped: true }) : null)
    setIsLoading(false)
  }

  const handlePaginationChange = useCallback((newPag) => {
    setPagination(prev => {
      if (
        prev.page === newPag.page &&
        prev.limit === newPag.limit &&
        prev.search === newPag.search &&
        Boolean(prev.isAll) === Boolean(newPag.isAll)
      ) {
        return prev
      }
      return { ...prev, ...newPag }
    })
  }, [])

  // Table Column Data
  let tableColumns = [
    {
      dataField: "id",
      text: "# ID",
      sort: true,
    },
    {
      dataField: "session",
      text: "Session",
      sort: true,
    },
    {
      dataField: "to_number",
      text: "Recipient",
      sort: true,
    },
    {
      dataField: "message",
      text: "Message",
      sort: false,
      formatter: (cell) => (
        <div style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          maxWidth: "350px",
          fontSize: "12px"
        }}>
          {cell}
        </div>
      )
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      formatter: (cell) => {
        if (cell === "sent") {
          return <Badge color="success">Sent</Badge>
        } else {
          return <Badge color="danger">Failed</Badge>
        }
      }
    },
    {
      dataField: "error_message",
      text: "Error Details",
      formatter: (cell) => cell || "-"
    },
    {
      dataField: "created_at",
      text: "Sent Time",
      sort: true,
    }
  ]

  const submitCall = async (filters = filterData) => {
    const isAllSelected = pagination?.isAll || pagination?.limit === 'all' || (typeof pagination?.limit === 'number' && pagination?.limit > 100);

    const payload = {
      ...filters,
      status: whatsappStatus
    }

    if (isAllSelected) {
      lastQueryRef.current = '';
      batchCancelRef.current = true;
      await new Promise(r => setTimeout(r, 60));
      batchCancelRef.current = false;
      isBatchRunningRef.current = true;

      setIsLoading(true);
      try {
        await runBatchLoader({
          fetchPage: async (page, batchSize) => {
            const queryParams = new URLSearchParams({
              searchTerm: pagination?.search || '',
              limit: batchSize,
              page: page,
            }).toString();
            const res = await post(`api/reports/whatsapp_logs?${queryParams}`, payload);
            return res?.status === 200 ? res.response : {};
          },
          batchSize: 100,
          initialTotal: totalRowsRef.current || 0,
          onProgress: setBatchProgress,
          onRowsLoaded: (accumulatedRows, totalCount) => {
            setRowData({
              total_rows: totalCount,
              rows: accumulatedRows
            });
          },
          cancelRef: batchCancelRef,
          runningRef: isBatchRunningRef,
        });
      } catch (err) {
        console.error("Error batch loading whatsapp logs:", err);
      } finally {
        setIsLoading(false);
      }
    } else {
      const queryParams = new URLSearchParams({
        searchTerm: pagination?.search || '',
        limit: pagination?.limit,
        page: pagination?.page,
      }).toString();
      const requestKey = `${queryParams}_${JSON.stringify(payload)}`;
      if (lastQueryRef.current === requestKey) return;
      lastQueryRef.current = requestKey;

      setIsLoading(true);
      setBatchProgress(null);
      try {
        const res = await post(`api/reports/whatsapp_logs?${queryParams}`, payload);
        if (res && res.status === 200) {
          setRowData(res.response);
        } else {
          setRowData([]);
        }
      } catch (err) {
        console.error("Error loading whatsapp logs:", err);
      } finally {
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
    submitCall()
  }, [filterData, pagination, whatsappStatus])

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Reports | {isType}</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Reports" breadcrumbItem={isType} />

          <Row className="mb-3 align-items-end">
            <Col md={9}>
              <Filters
                callback={setFilterData}
                filterOptions={{
                  order_type: false,
                  city: false,
                  client: false,
                  dates: true,
                  category: false,
                  p_type: false,
                  product: false,
                }}
              />
            </Col>
            <Col md={3} className="mb-3">
              <label>WhatsApp Status</label>
              <select
                className="form-control"
                value={whatsappStatus}
                onChange={(e) => setWhatsappStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="sent">Sent Successfully</option>
                <option value="failed">Failed Delivery</option>
              </select>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <Datatable
                    keyField={"id"}
                    filename={`WhatsApp_Logs - ${moment().format(
                      "DDMMYYYYHHmmSS"
                    )}`}
                    title={``}
                    isAdd={false}
                    handleAddButton=""
                    isSearch={true}
                    excel={true}
                    csv={false}
                    pdf={false}
                    columns={tableColumns}
                    rowsLength={rowData?.total_rows || (Array.isArray(rowData) ? rowData.length : 0)}
                    rows={rowData?.rows || (Array.isArray(rowData) ? rowData : [])}
                    isTableHead={true}
                    loading={isLoading && (!batchProgress || batchProgress.loaded === 0)}
                    batchProgress={batchProgress}
                    onCancelBatch={handleCancelBatch}
                    batchSize={100}
                    is_remote={true}
                    ssr={handlePaginationChange}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default WNotificationReport
